import { useEffect, RefObject } from 'react';
import type { TypesetterWorkerClient } from '../../../../workers/TypesetterWorkerClient';
import { CanvasLogger } from '../../../../../../utils/loggers/CanvasLogger';
export interface DocumentStateSyncDependencies {
  documentState: { text: string; pageSetup?: any; fontSettings?: any } | null | undefined;
  isTypesetterReady: boolean;
  typesetterClient: TypesetterWorkerClient | null;
  currentDocumentTextRef: RefObject<string>;
  lastLocalTextUpdateRef: RefObject<string | null>;
  setRenderTrigger: (updater: (prev: number) => number) => void;
  setStableInputText: (updater: (prev: string) => string) => void;
  setFontFamily: (value: string) => void;
  setFontSizePx: (value: number) => void;
}

/**
 * Synchronizes document text from parent state to internal refs, preventing race conditions
 * and cursor resets during local edits.
 */
export function setupDocumentTextSync({
  documentState,
  isTypesetterReady,
  typesetterClient,
  currentDocumentTextRef,
  lastLocalTextUpdateRef,
  setRenderTrigger
}: Pick<DocumentStateSyncDependencies,
  'documentState' | 'isTypesetterReady' | 'typesetterClient' |
  'currentDocumentTextRef' | 'lastLocalTextUpdateRef' | 'setRenderTrigger'>) {
  return useEffect(() => {
    const parentText = documentState?.text || '';
    const refText = currentDocumentTextRef.current || '';

    // ✅ CRITICAL FIX: Ignore stale prop updates when ref has newer text
    // This prevents VirtualPagesV3 re-renders with stale props from overwriting correct local edits
    // The ref is the source of truth during active editing
    const isRefAhead = refText.length > parentText.length;
    const isParentAhead = parentText.length > refText.length;
    const isSameLength = refText.length === parentText.length;
    
    // If ref is ahead, completely ignore the prop update (stale state from React propagation delay)
    if (isRefAhead) {
      CanvasLogger.state.stale('ref has newer text', {
        refLength: refText.length,
        propLength: parentText.length,
        refPreview: refText.substring(0, 20),
        propPreview: parentText.substring(0, 20)
      });
      return; // Don't sync, don't trigger render - ref is source of truth
    }

    // ✅ FIX: Don't overwrite if we just made a local update with the same text
    // This prevents the parent's async state update from reverting our local changes
    // AND prevents the input handler from resetting (which causes cursor to jump to position 0)
    if (lastLocalTextUpdateRef.current === parentText) {
      CanvasLogger.state.skip('parent text matches last local update');
      lastLocalTextUpdateRef.current = null; // Clear flag
      return;
    }

    // ✅ CRITICAL: Only update if text actually changed AND it's not from our own update
    // This prevents flickering and cursor resets when React state updates
    const textChanged = refText !== parentText;
    if (textChanged) {
      const wasEmpty = refText.length === 0;
      const isInitialLoad = wasEmpty && parentText.length > 0;

      CanvasLogger.state.skip('parent text changed', {
        refLength: refText.length,
        parentLength: parentText.length,
        isOurUpdate: lastLocalTextUpdateRef.current === parentText,
        isInitialLoad,
        isParentAhead,
        willSync: isInitialLoad || isParentAhead
      });

      // Only sync when parent is ahead (external update) or on initial load
      // Ref being ahead is already handled above (early return)
      if (isInitialLoad || isParentAhead) {
        currentDocumentTextRef.current = parentText;
        lastLocalTextUpdateRef.current = null; // Clear flag

        // ✅ CRITICAL FIX: Trigger render on initial load with text
        // When document loads with text, we need to render it immediately
        if (isInitialLoad && isTypesetterReady && typesetterClient) {
          console.log('📝 [CanvasV3] Initial load with text - triggering render');
          setRenderTrigger(prev => prev + 1);
        }
      } else if (isSameLength && refText !== parentText) {
        // Same length but different content - could be a replacement
        // Only sync if parent text is actually different (not just a stale prop)
        // This handles cases where external updates replace text at same length
        console.log('📝 [CanvasV3] Same length but different content - syncing (external replacement)');
        currentDocumentTextRef.current = parentText;
        lastLocalTextUpdateRef.current = null;
      }
    }
  }, [documentState?.text, isTypesetterReady, typesetterClient]);
}

/**
 * Synchronizes font settings from document metadata to component state.
 */
export function setupFontSettingsSync({
  documentState,
  setFontFamily,
  setFontSizePx
}: Pick<DocumentStateSyncDependencies, 'documentState' | 'setFontFamily' | 'setFontSizePx'>) {
  return useEffect(() => {
    const newFontFamily = (documentState as any)?.fontSettings?.fontFamily?.replace(/'/g, '') || 'Inter';
    const newFontSizePx = (documentState as any)?.fontSettings?.fontSizePx || 14;

    setFontFamily(newFontFamily);
    setFontSizePx(newFontSizePx);
  }, [documentState]);
}

/**
 * Synchronizes stable input text state for external changes, preventing input handler resets.
 */
export function setupStableInputTextSync({
  documentState,
  lastLocalTextUpdateRef,
  setStableInputText
}: Pick<DocumentStateSyncDependencies, 'documentState' | 'lastLocalTextUpdateRef' | 'setStableInputText'>) {
  return useEffect(() => {
    const parentText = documentState?.text || '';
    // If this is our own update, don't update stableInputText (prevents input handler reset)
    if (lastLocalTextUpdateRef.current === parentText) {
      console.log('📝 [CanvasV3] Skipping stableInputText update - this is our own React state update');
      return; // Skip update - this is our own React state update
    }
    // External update - update stable text state
    // Use functional update to avoid dependency on stableInputText
    setStableInputText(prev => {
      if (prev !== parentText) {
        console.log('📝 [CanvasV3] Updating stableInputText for external change:', {
          oldLength: prev.length,
          newLength: parentText.length,
          isInitialLoad: prev === ''
        });
        return parentText;
      }
      return prev; // No change needed
    });
  }, [documentState?.text]);
}
